/* eslint-disable prefer-const */
/* eslint-disable no-var */
// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

// Ionic
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  ModalController
} from '@ionic/angular/standalone';

// Third party
import * as _ from "lodash";
import { Subscription, take, takeUntil } from 'rxjs';
import moment from 'moment';
import { AgChartsAngular } from "ag-charts-angular";
import { AgChartLegendClickEvent } from "ag-charts-community";

// Local library
import {
  ConvertTimeToMinutesPipe,
  ConvertToRangePipe,
  FairValuePipe,
  MatrixDataPipe,
  NumberWithCommasPipe,
  PoolTotalPipe,
  ToAnyPipe,
  ToStringPipe,
  UnsubscribeObservableService
} from '@amwager/util';
import {
  IGetEventsDetailsResponse,
  IPool,
  IPoolTotals,
  IRunner,
  IToteMatrixSlimModel,
  IWillPaysToteResponse,
  TRACK_COLOR,
  TRACK_TYPE_lIST,
  arrangewagermatrix_removedup_dashes,
  toDictionary
} from '@amwager/util-ts';
import {
  ISelectedRace,
  ISelectedRaceAllOddsMatrixes,
  ISelectedRaceMatrix,
  ISelectedRacePoolsTotals,
  ISelectedRaceWillPays,
  WagerFacade
} from '@amwager/wager/data-access';



interface IWPSPoolsTotals {
  WIN: IPoolTotals & {TakeoutRates: number},
  PLC: IPoolTotals & {TakeoutRates: number},
  SHW: IPoolTotals & {TakeoutRates: number}
};

@Component({
  selector: 'feature-ion-amwager3-matrix',
  templateUrl: './matrix.component.html',
  styleUrls: ['./matrix.component.scss'],
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    IonContent,
    IonItem,
    IonInput,
    IonIcon,
    FormsModule,
    CommonModule,
    IonSegment,
    IonSegmentButton,
    MatrixDataPipe,
    NumberWithCommasPipe,
    PoolTotalPipe,
    ToStringPipe,
    ToAnyPipe,
    ConvertToRangePipe,
    FairValuePipe,
    ConvertTimeToMinutesPipe,
    AgChartsAngular
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatrixComponent extends UnsubscribeObservableService implements OnInit, AfterViewInit {

  private runId!: string;
  private eventId!: string;
  private raceId: number = 0;

  private raceAllOddsMatrixes?: {
    WIN?: IToteMatrixSlimModel[],
    TRU?: IToteMatrixSlimModel[]
  };

  private selectedEventTrack?: IGetEventsDetailsResponse;
  private getRaceAllOddsMatrixesSubs!: Subscription;


  readonly ODDS = 'Odds';
  readonly TOTALS = 'Totals';
  readonly WIN = 'WIN';
  readonly PLC = 'PLC';
  readonly SHW = 'SHW';
  readonly TRU = 'TRU';
  readonly EX = 'EX';
  readonly QU = 'QU';
  readonly DBL = 'DBL';
  readonly WILL_PAYS = 'WillPays';
  readonly WIN_CHART = 'Win Chart';
  readonly WIN_TABLE = 'Win Table';

  readonly TRACK_TYPE_lIST = TRACK_TYPE_lIST;

  winChartFilterButtons = {
    WIN: true,
    TRU: false,
    BOTH: false,
  };

  runners!: IRunner[];
  winTable: any[] = [];
  poolList!: string[];

  willPays!: IWillPaysToteResponse;
  selectedRace?: ISelectedRace;

  tab: string = this.ODDS;

  matrix: any;

  singleLegDict!: IWPSPoolsTotals;
  multiLegDict!: any;
  multiRaceDict!: any;

  // Chart Options
  winChartOptions: any;
  winChartOptionsCopy: any;
  @ViewChild(AgChartsAngular) agCharts!: any;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly wagerFacade: WagerFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    private readonly destroyRef: DestroyRef,
  ) {

    super();

    this.observerGetSelectedEventTrack();
    this.observerGetSelectedRace();
    this.observerGetRaceMatrix();
    this.observerGetRacePoolsTotals();
    this.observerGetRaceWillPays();

  }

  ngOnInit(): void {
    this.winTableBuild();
    this.winChartBuild();
  }

  ngAfterViewInit(): void {
   this.selectTabMenu(0, this.ODDS);
  }

  /**
   * Observers
   */
  observerGetSelectedEventTrack(): void {
    this.wagerFacade.getSelectedEventTrack$()
    .pipe(take(1))
    .subscribe((selectedEventTrack?: IGetEventsDetailsResponse) => {
      if (!selectedEventTrack) return;

      this.selectedEventTrack = selectedEventTrack;
      this.eventId = (selectedEventTrack as any)?.id;
      this.runId = selectedEventTrack?.ToteResponse?.RunId;
    });
  }

  observerGetSelectedRace(): void {
    this.wagerFacade.getSelectedRace$()
    .pipe(take(1))
    .subscribe((race?: ISelectedRace) => {
      if (!race || !race?.RaceId) return;

      this.selectedRace = race;
      this.runners = race?.Runners || [];
      this.raceId = race?.RaceId;

      this.poolList = [
        'TRU',
        ...race?.PoolList?.split(",") || [],
      ];


      this.cdRef.markForCheck(); // re-render for template
    });

  }

  observerGetRaceMatrix(): void {
    this.wagerFacade.getRaceMatrix$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((matrix?: ISelectedRaceMatrix) => {
      if (_.isEmpty(matrix)) return;

      // build matrixes
      _.map(this.poolList, (poolId: string) => {
        this.matrix = {
          ...this.matrix,
          [poolId]: null
        };

        try {
          this.matrix[poolId] = matrix?.[poolId]?.response?.ToteResponse;
        } catch (error) {
          null
        }
      });

      this.updateMatrix(this.matrix, 'EX');
      this.updateMatrix(this.matrix, 'QU');
      this.updateMatrix(this.matrix, 'DBL');
    });
  }

  observerGetRacePoolsTotals(): void {
    this.wagerFacade.getRacePoolsTotals$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((poolsTotals?: ISelectedRacePoolsTotals) => {
      const racePoolsTotals = poolsTotals?.response?.ToteResponse;

      if (_.isEmpty(racePoolsTotals)) return;

      let singleLeg: any[] = [];
      let multiLeg: any[] = [];
      let multiRace: any[] = [];

      // build wager category
      _.forEach(this.selectedRace?.Pools, (pool: IPool) => {
        if (pool?.PoolRaces === 1) {
          if (pool?.Legs === 1) singleLeg?.push({ poolId: pool?.PoolId, id: pool?.PoolId });
          else multiLeg?.push({ poolId: pool?.PoolId, id: pool?.PoolId });
        }
        else multiRace?.push({ poolId: pool?.PoolId, id: pool?.PoolId });
      });

      _.forEach(racePoolsTotals?.PoolTotals, (pool: IPoolTotals) => {
        // update single leg
        singleLeg = _.map(singleLeg, (leg) => {
          if (leg?.poolId === pool?.PoolId) return {
            ...leg,
            ...pool,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[leg?.poolId]
          }

          return {
            ...leg,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[leg?.poolId]
          };
        });

        // update multiple leg
        multiLeg = _.map(multiLeg, (leg) => {
          if (leg?.poolId === pool?.PoolId) return {
            ...leg,
            ...pool,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[leg?.poolId]
          }

          return {
            ...leg,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[leg?.poolId]
          };
        });

        // update multi race
        multiRace = _.map(multiRace, (race) => {
          if (race?.poolId === pool?.PoolId) return {
            ...race,
            ...pool,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[race?.poolId]
          }

          return {
            ...race,
            TakeoutRates: (this.selectedEventTrack?.TakeoutRates as any)?.[race?.poolId]
          };
        });

      });

      this.singleLegDict = toDictionary(singleLeg) as IWPSPoolsTotals;
      this.multiLegDict = toDictionary(multiLeg);
      this.multiRaceDict = toDictionary(multiRace);

      this.cdRef.markForCheck();
    });
  }

  observerGetRaceWillPays(): void {
    this.wagerFacade.getRaceWillPays$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((willPaysResponse?: ISelectedRaceWillPays) => {
      const willPays = willPaysResponse?.response?.ToteResponse;

      if (_.isEmpty(willPays)) return;

      this.willPays = willPays;
    });
  }


  updateMatrix(matrix: any, type: 'EX' | 'QU' | 'DBL'): void {
    const TO_UPDATE_MATRIX = matrix[type] || undefined;

    if (_.isEmpty(TO_UPDATE_MATRIX)) return;

    let minPrice = 1000000;

    _.map(TO_UPDATE_MATRIX?.Rows, (rowEx) => {
      _.map(rowEx?.Columns, (colEx) => {
        if (colEx?.Price && !colEx?.Invalid && minPrice > colEx?.Price) {
          minPrice = colEx.Price
        }
      });
    });

    // build matrix rows
    const MATRIX_ROWS = _.map(TO_UPDATE_MATRIX?.Rows, (rowEx, indexRow) => {
      const COLUMNS = _.map(rowEx?.Columns, (colEx, indexCol) => {
        let firstOdd = 0;
        let secondOdd = 0;
        let currentPrice = 0;
        let fairValue = null;
        let fairValueClass = '';

        if (colEx?.Invalid) return colEx;

        // EX pool
        if (type === 'EX') {
          firstOdd = (Number(this.matrix['WIN']?.Rows[0]?.Columns[indexRow]?.Price) || 0)
            / ((this.matrix['WIN']?.PriceBaseWager/2) || 0);
          secondOdd = (Number(this.matrix['WIN']?.Rows[0]?.Columns[indexCol]?.Price) || 0)
            / ((this.matrix['WIN']?.PriceBaseWager/2) || 0);

          fairValue = (firstOdd * (secondOdd + 1));
          currentPrice = colEx.Price / (TO_UPDATE_MATRIX.PriceBaseWager/2);

          if (fairValue > 0) {
            if (fairValue <= currentPrice * 0.75)
              fairValueClass = "fairValueLesser";
            else if (fairValue >= currentPrice * 1.25)
              fairValueClass = "fairValueGreater";
          }
        }

        return {
          ...colEx,
          data: colEx?.Price/(TO_UPDATE_MATRIX?.PriceBaseWager/2) || '-',
          fairValueClass: fairValueClass || '',
          favouriteBetClass: minPrice === colEx.Price ? 'favouriteBet' : ''
        }
      });

      return {
        ...rowEx,
        Columns: COLUMNS
      }
    });

    // add matrix rows
    this.matrix[type] = {
      ...TO_UPDATE_MATRIX,
      Rows: MATRIX_ROWS
    };

    this.cdRef.markForCheck();
  }

  /**
   *
   * Modals
   */
  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  close() {
    // return this.modalCtrl.dismiss(this.name, 'confirm');
    return this.modalCtrl.dismiss(null, 'cancel');
  }


  selectTabMenu(tabMenu: HTMLElement | number, type: string): void {
    const tabHeader: HTMLElement[] = this.elementRef.nativeElement.querySelectorAll('.tab-indicator');

    if (!tabHeader) return;

    tabHeader.forEach((header: HTMLElement) => {
      header.classList.remove('active');
    });

    this.tab = type;

    if (typeof tabMenu === "number") {
      tabHeader[0].classList.add('active');
      tabHeader[0].focus();
      tabHeader[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    else {
      tabMenu.classList.add('active');
      tabMenu.focus();
      tabMenu.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  winTableBuild(): void {
    this.wagerFacade.getRaceAllOddsMatrixes$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((raceAllOddsMatrixesResponse?: {
      WIN?: ISelectedRaceAllOddsMatrixes
    }) => {
      this.raceAllOddsMatrixes = {
        WIN: raceAllOddsMatrixesResponse?.WIN?.response?.ToteMatrixSlim || []
      };

      const winRaceAllOddsMatrixes: IToteMatrixSlimModel[] = this.raceAllOddsMatrixes?.WIN || [];

      if (_.isEmpty(winRaceAllOddsMatrixes)) return;

      let winTablePreviousData: any;
      // eslint-disable-next-line prefer-const
      let previousMatrixDataOddList: any[] = [];

      _.forEach(winRaceAllOddsMatrixes, (odds) => {
        const localTime = new Date(odds.TimeStamp + 'Z');
        // eslint-disable-next-line prefer-const
        let total: any[] = [];

        let hours: string | number = localTime.getHours();
        let meridiem = '';
        let minutes: string | number = localTime.getMinutes();
        let seconds: string | number = localTime.getSeconds();
        let currentTime = '';

        if (hours >= 12) {
          meridiem = 'PM';
          hours  = hours - 12;
        }
        else {
          meridiem = 'AM';
        }

        hours  = hours  < 10 ? ('0' + hours) : hours;
        minutes = minutes < 10 ? ('0' + minutes) : minutes;
        seconds = seconds < 10 ? ('0' + seconds) : seconds;
        currentTime = hours + ':' + minutes+':' + seconds + ' ' + meridiem;

        if (!winTablePreviousData) winTablePreviousData = odds;

        let alreadyExist = false;
        _.map(previousMatrixDataOddList, (data) => {
          if (data?.time === currentTime) alreadyExist = true;
        });

        if (alreadyExist) return;

        _.forEach(odds?.Row?.[0]?.Columns, (col, colIndex) => {
          let previousData: any = winTablePreviousData?.Row?.[0]?.Columns;
          let remark;

          previousData = previousData?.[colIndex];

          if (_.isEmpty(previousData)
            || _.isEmpty(col)
            || !previousData?.DecimalOdds
            || !col?.DecimalOdds
            || (col.DecimalOdds === previousData.DecimalOdds)
          ) remark = '';
          else remark = (col.DecimalOdds||0) < previousData.DecimalOdds ? 'increase': 'decrease';

          total.push({
            Runner: col.ColumnNumber,
            odds: col.DecimalOdds || '-',
            odd_remark: remark,
            cur: col.DecimalOdds,
            prev: previousData.DecimalOdds || ''
          });
        });

        if (previousMatrixDataOddList?.length >= 5) previousMatrixDataOddList.pop();

        previousMatrixDataOddList.unshift({
          time: currentTime,
          totals: total
        });
        winTablePreviousData = odds;

      });

      this.winTable = previousMatrixDataOddList;

      this.cdRef.markForCheck();
    });
  }

  /**
   * Charts
   */
  winChartFilter(pool: 'WIN' | 'TRU' | 'BOTH' | 'ALL' | 'CLEAR'): void {
    // TODO: ALL button function
    // if (pool === 'ALL') {
    //   this.agCharts = {
    //     ...this.agCharts,
    //     chart: {
    //       ...this.agCharts?.chart,
    //       chart: {
    //         ...this.agCharts?.chart?.chart,
    //         legend: {
    //           ...this.agCharts?.chart?.chart?.legend,
    //           _data: _.map(this.agCharts?.chart?.chart?.legend?._data, (data) => {
    //             return {
    //               ...data,
    //               enabled: true
    //             }
    //           }) || []
    //         }
    //       }
    //     }
    //   }
    //   this.cdRef.markForCheck();
    //   return;
    // }

    // if (pool === 'CLEAR') {
    //   this.agCharts = {
    //     ...this.agCharts,
    //     chart: {
    //       ...this.agCharts?.chart,
    //       chart: {
    //         ...this.agCharts?.chart?.chart,
    //         legend: {
    //           ...this.agCharts?.chart?.chart?.legend,
    //           _data: _.map(this.agCharts?.chart?.chart?.legend?._data, (data) => {
    //             return {
    //               ...data,
    //               enabled: false
    //             }
    //           }) || []
    //         }
    //       }
    //     }
    //   }

    //   this.cdRef.markForCheck();
    //   return;
    // }

    if (pool === 'ALL' || pool === 'CLEAR') return;

    this.winChartFilterButtons.WIN = false;
    this.winChartFilterButtons.TRU = false;
    this.winChartFilterButtons.BOTH = false;

    this.winChartFilterButtons[pool] = true;

    if (pool === 'BOTH') {
      this.winChartBothBuild();
      return;
    }

    this.winChartBuild();
    this.cdRef.markForCheck();
  }

  winChartBuild(): void {
    if (this.getRaceAllOddsMatrixesSubs) this.getRaceAllOddsMatrixesSubs.unsubscribe();

    this.getRaceAllOddsMatrixesSubs = this.wagerFacade.getRaceAllOddsMatrixes$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((raceAllOddsMatrixesResponse?: {
      WIN?: ISelectedRaceAllOddsMatrixes,
      TRU?: ISelectedRaceAllOddsMatrixes
    }) => {
      this.winChartOptions = {};

      if (this.winChartFilterButtons.BOTH) return;

      this.raceAllOddsMatrixes = {
        WIN: raceAllOddsMatrixesResponse?.WIN?.response?.ToteMatrixSlim || [],
        TRU: raceAllOddsMatrixesResponse?.TRU?.response?.ToteMatrixSlim || []
      };

      let winRaceAllOddsMatrixes: IToteMatrixSlimModel[] = [];
      let winChartToShow: any[] = [];

      if (this.winChartFilterButtons.WIN)
        winRaceAllOddsMatrixes = this.raceAllOddsMatrixes?.WIN || [];

      if (this.winChartFilterButtons.TRU)
        winRaceAllOddsMatrixes = this.raceAllOddsMatrixes?.TRU || [];

      if (_.isEmpty(winRaceAllOddsMatrixes)) return;

      // build point axis
      const tempDataWINChart = this.buildChartPoints(winRaceAllOddsMatrixes);

      // select latest 20 data
      winChartToShow = arrangewagermatrix_removedup_dashes(tempDataWINChart).splice(-20);

      // list of scratched runner
      let scratched: any[] = [];
      _.forEach(this.selectedRace?.Runners, (runner) => {
        if (runner?.Scratch) scratched.push(runner?.RunnerId);
      });

      const arrayData = _.filter(_.map(winChartToShow, (winData) => {
        if (scratched.includes(winData?.color)) return undefined;

        return {
          ...winData,
          data: _.map(winData?.data, (dataPoint) => {
            return {
              [`odds${winData?.color}`]: dataPoint[1] || 0,
              [`mtp${winData?.color}`]: dataPoint[0] || 0,
            }
          }) || []
        }
      }), dataFilter => dataFilter);

      this.buildChartOptions(arrayData);
    });
  }

  getColorRunner(index: number): string {
    let color = TRACK_COLOR[TRACK_TYPE_lIST[this.selectedRace?.TrackType || 1]]?.[index];

    if (color === 'white') color = '#e2e2e2';
    if (color === 'yellow') color = '#c5c50f';

    return color;
  }

  buildChartPoints(matrix?: IToteMatrixSlimModel[]): any[] {
    let tempDataWINChart: any[] = [];

    if (_.isEmpty(matrix)) return tempDataWINChart;

    const postTime = this.selectedRace?.PostTime?.replace('Z', '');

    // build point axis
    _.forEach(matrix, (odds) => {
      const time = moment(postTime).diff(moment(odds?.TimeStamp?.replace('Z', '')), 'minutes', true);

      _.forEach(odds?.Row?.[0]?.Columns, (col, colIndex) => {
        if (time > 20) return;
        if (_.isEmpty(tempDataWINChart[colIndex])) {
          tempDataWINChart.push({
            points: {
              show: false
            },
            lines: {
              show: true,
              width: 2
            },
            color: col.ColumnNumber || '',
            label: '#' + col.ColumnNumber,
            data: [[time, col.DecimalOdds]],
          });
        }
        else {
          tempDataWINChart[colIndex]?.data.push([time, col.DecimalOdds]);
        }
      });
    });

    return tempDataWINChart;
  }

  buildChartOptions(arrayData: any[]): void {
    let seriesData: any[] = [];

    if (this.winChartFilterButtons.BOTH) {
      seriesData = _.map(arrayData, (data) => {
        return {
          type: "line",
          xKey: data?.xKey,
          yKey: data?.yKey,
          yName: `#${data?.color}`,
          lineDash: data?.pool === 'WIN' ? [0, 0] : [4,2],
          marker: {
            fill: this.getColorRunner(data?.color),
            size: 5,
            stroke: this.getColorRunner(data?.color),
            strokeWidth: 1,
            shape: data?.pool === 'WIN' ? 'diamond' : 'circle',
          },
          stroke: this.getColorRunner(data?.color),
          strokeWidth: 1
        }
      })
    }
    else {
      seriesData = _.map(arrayData, (data) => {
        return {
          type: "line",
          xKey: `mtp${data?.color}`,
          yKey: `odds${data?.color}`,
          yName: `#${data?.color}`,
          marker: {
            fill: this.getColorRunner(data?.color),
            size: 5,
            stroke: this.getColorRunner(data?.color),
            strokeWidth: 1,
            shape: this.winChartFilterButtons.WIN
              ? 'diamond'
              : 'circle',
          },
          stroke: this.getColorRunner(data?.color),
          strokeWidth: 1,
        }
      })
    }

    this.winChartOptions = {
      data: _.flatten(_.map(arrayData, (data) => {
        return data?.data || []
      })),
      theme: {
        overrides: {
          line: {
            series: {
              highlightStyle: {
                series: {
                  dimOpacity: 0.2,
                  strokeWidth: 2,
                  size: 8,
                },
              },
            },
          },
        },
      },
      series: seriesData,
      axes: [
        {
          type: "number",
          position: "bottom",
          label: {
            avoidCollisions: true,
          },
          reverse: true,
          min: -2,
          nice: true,
          tick: {
            interval: 2
          },
          title: {
            text: "Minutes To Post",
          },
          gridLine: {
            style: [
              {
                stroke: "white"
              },
            ],
          },
        },
        {
          type: "number",
          position: "left",
          label: {
            minSpacing: 100,
            avoidCollisions: true,
          },
          min: 1,
          nice: true,
          tick: {
            interval: 10
          },
          title: {
            text: (this.winChartFilterButtons.WIN
              ? 'WIN'
              : this.winChartFilterButtons.TRU
                ? 'TRU'
                : this.winChartFilterButtons.BOTH
                  ? 'Both'
                  : '') + ' Odds'
            ,
          },
          gridLine: {
            style: [
              {
                stroke: "#e2e2e2"
              },
            ],
          },

        },
      ],
      legend: {
        item: {
          paddingY: 15,
        },
      },
    };

    this.winChartOptionsCopy = _.cloneDeep(this.winChartOptions);

    this.cdRef.markForCheck();
  }

  winChartBothBuild(): void {
    if (!this.winChartFilterButtons.BOTH) return;

    this.wagerFacade.getRaceAllOddsMatrixes$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((raceAllOddsMatrixesResponse?: {
      WIN?: ISelectedRaceAllOddsMatrixes,
      TRU?: ISelectedRaceAllOddsMatrixes
    }) => {
      if (!this.winChartFilterButtons.BOTH) return;

      this.winChartOptions = {};

      this.raceAllOddsMatrixes = {
        WIN: raceAllOddsMatrixesResponse?.WIN?.response?.ToteMatrixSlim || [],
        TRU: raceAllOddsMatrixesResponse?.TRU?.response?.ToteMatrixSlim || []
      };

      if (_.isEmpty(this.raceAllOddsMatrixes.WIN) && _.isEmpty(this.raceAllOddsMatrixes.TRU)) return;

      // eslint-disable-next-line prefer-const
      let tempDataWINChartWIN: any[] = this.buildChartPoints(this.raceAllOddsMatrixes.WIN);
      let tempDataWINChartTRU: any[] = this.buildChartPoints(this.raceAllOddsMatrixes.TRU);

      // select latest 20 data
      let winChartToShowWIN: any[] = arrangewagermatrix_removedup_dashes(tempDataWINChartWIN).splice(-20);
      let winChartToShowTRU: any[] = arrangewagermatrix_removedup_dashes(tempDataWINChartTRU).splice(-20);

      // list of scratched runner
      let scratched: any[] = [];
      _.forEach(this.selectedRace?.Runners, (runner) => {
        if (runner?.Scratch) scratched.push(runner?.RunnerId);
      });

      const arrayDataWIN = _.filter(_.map(winChartToShowWIN, (winData) => {
        if (scratched.includes(winData?.color)) return undefined;

        return {
          ...winData,
          data: _.map(winData?.data, (dataPoint) => {
            return {
              [`odds${winData?.color}WIN`]: dataPoint[1] || 0,
              [`mtp${winData?.color}WIN`]: dataPoint[0] || 0,
            }
          }) || [],
          xKey: `mtp${winData?.color}WIN`,
          yKey: `odds${winData?.color}WIN`,
          pool: 'WIN'
        }
      }), dataFilter => dataFilter);

      const arrayDataTRU = _.filter(_.map(winChartToShowTRU, (winData) => {
        if (scratched.includes(winData?.color)) return undefined;

        return {
          ...winData,
          data: _.map(winData?.data, (dataPoint) => {
            return {
              [`odds${winData?.color}TRU`]: dataPoint[1] || 0,
              [`mtp${winData?.color}TRU`]: dataPoint[0] || 0,
            }
          }) || [],
          xKey: `mtp${winData?.color}TRU`,
          yKey: `odds${winData?.color}TRU`,
          pool: 'TRU'
        }
      }), dataFilter => dataFilter);

      const arrayData = [ ...arrayDataWIN, ...arrayDataTRU ];

      this.buildChartOptions(arrayData);
    });
  }

}
